/**
 * MALE body rest skeleton — the Manny hierarchy (same 89 bones, names, order and bone-frame
 * conventions, so every clip binds unchanged) with every joint moved to the male mesh's
 * anatomy. Source geometry is uniformly 0.4223x to metres with declared head/finger fit edits;
 * this rig is what the avatar builds its bones from, so in-game proportions ARE the asset's.
 * Base rig: build-male.mts. Finger landmarks: saved male-articulation.json; publish-articulation.mts.
 *
 * Tuple: [name, parentIndex, px, py, pz, qx, qy, qz, qw] (locals, metres)
 */
import type { MannyBoneDef } from "./mannyRig";

export const MALE_RIG: readonly MannyBoneDef[] = [
  ["root", -1, 0, -0.007508, 0, -0.707107, 0, 0, 0.707107],
  ["pelvis", 0, 0, 0.008445, 1.046365, -0.003607, -0.707097, 0.003607, 0.707098],
  ["spine_01", 1, 0.026689, 0, 0, 0, 0, 0.000001, 1],
  ["spine_02", 2, 0.04854, 0, 0, 0, 0, 0, 1],
  ["spine_03", 3, 0.052208, 0, 0, 0, 0, -0.000001, 1],
  ["spine_04", 4, 0.061871, 0, 0, 0.000004, -0.000001, 0, 1],
  ["spine_05", 5, 0.139461, 0, 0, -0.000004, 0.000001, 0, 1],
  ["neck_01", 6, 0.085108, 0, 0, 0, 0, -0.087563, 0.996159],
  ["neck_02", 7, 0.039143, 0, 0, -0.000001, 0, 0, 1],
  ["head", 8, 0.037919, 0, 0, 0.000001, 0, 0.1862, 0.982512],
  ["clavicle_l", 6, 0.026075, 0.009049, -0.023226, 0.048598, 0.810265, 0.03999, 0.582674],
  ["upperarm_l", 10, 0.172898, 0, 0, 0.008452, 0.374489, -0.035056, 0.92653],
  ["lowerarm_l", 11, 0.223666, 0, 0, 0.060805, 0.057867, -0.235229, 0.968309],
  ["lowerarm_twist_02_l", 12, 0.066771, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["lowerarm_twist_01_l", 12, 0.133542, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["hand_l", 12, 0.200313, 0, 0, -0.55488, 0.045119, 0.108561, 0.823582],
  ["middle_metacarpal_l", 15, 0.034931, -0.007798, -0.001893, -0.037291, -0.000386, -0.020259, 0.999099],
  ["middle_01_l", 16, 0.089667, -0.0085674, -0.0260843, -0.2692211, -0.0600679, 0.3252472, 0.9045032],
  ["middle_02_l", 17, 0.0303177, 0, 0, 0, 0, 0, 1],
  ["middle_03_l", 18, 0.0256535, 0, 0, 0, 0, 0, 1],
  ["pinky_metacarpal_l", 15, 0.034296, -0.003165, 0.024742, -0.218231, -0.188166, 0.058286, 0.955809],
  ["pinky_01_l", 20, 0.0767174, 0.0123273, -0.022944, -0.0225159, 0.1292829, 0.4558309, 0.8803392],
  ["pinky_02_l", 21, 0.021733, 0, 0, 0, 0, 0, 1],
  ["pinky_03_l", 22, 0.0183895, 1e-7, 0, 0, 0, 0, 1],
  ["ring_metacarpal_l", 15, 0.034916, -0.005614, 0.011297, -0.116599, -0.100569, -0.02566, 0.987741],
  ["ring_01_l", 24, 0.0806731, 0.0092175, -0.0298315, -0.1586943, -0.0060705, 0.4302107, 0.8886496],
  ["ring_02_l", 25, 0.0276752, 0, 0, 0, 0, 0, 1],
  ["ring_03_l", 26, 0.0234174, 0, 0, 0, 0, 0, 1],
  ["thumb_01_l", 15, 0.0713822, -0.0101442, -0.0671414, 0.8995575, 0.243861, -0.139838, 0.3343252],
  ["thumb_02_l", 28, 0.0275464, 1e-7, 0, 0, 0, 0, 1],
  ["thumb_03_l", 29, 0.0233084, 0, 0, 0, 0, 0, 1],
  ["index_metacarpal_l", 15, 0.035642, -0.003981, -0.02462, 0.028966, 0.063705, -0.007109, 0.997523],
  ["index_01_l", 31, 0.0889424, -0.0351845, -0.0153433, -0.3341596, -0.1342812, 0.14248, 0.9219574],
  ["index_02_l", 32, 0.0280499, 0, 0, 0, 0, 0, 1],
  ["index_03_l", 33, 0.0237347, 0, 0, 0, 0, 0, 1],
  ["upperarm_twist_01_l", 11, 0.074555, 0, 0, -0.000054, -0.01879, -0.00285, 0.999819],
  ["upperarm_twist_02_l", 11, 0.149111, 0, 0, 0, 0, 0, 1],
  ["clavicle_r", 6, 0.026075, 0.009049, 0.023227, 0.810259, -0.048599, -0.582683, 0.03999],
  ["upperarm_r", 37, -0.172898, 0, 0.000004, 0.008451, 0.3745, -0.035056, 0.926525],
  ["lowerarm_r", 38, -0.223667, 0, 0, 0.060804, 0.057867, -0.23523, 0.968308],
  ["lowerarm_twist_02_r", 39, -0.066771, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["lowerarm_twist_01_r", 39, -0.133542, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["hand_r", 39, -0.200313, 0, 0, -0.55488, 0.045131, 0.108571, 0.82358],
  ["middle_metacarpal_r", 42, -0.03493, 0.007802, 0.001891, -0.037291, -0.000386, -0.020259, 0.999099],
  ["middle_01_r", 43, -0.0896672, 0.0085655, 0.0260856, -0.2692214, -0.0600688, 0.325233, 0.9045082],
  ["middle_02_r", 44, -0.0303177, 0, 0, 0, 0, 0, 1],
  ["middle_03_r", 45, -0.0256535, 0, 0, 0, 0, 0, 1],
  ["pinky_metacarpal_r", 42, -0.034298, 0.003165, -0.024744, -0.218231, -0.188166, 0.058286, 0.955809],
  ["pinky_01_r", 47, -0.0767143, -0.0123261, 0.0229451, -0.0225169, 0.1292859, 0.4558176, 0.8803456],
  ["pinky_02_r", 48, -0.021733, 0, 0, 0, 0, 0, 1],
  ["pinky_03_r", 49, -0.0183895, -1e-7, 0, 0, 0, 0, 1],
  ["ring_metacarpal_r", 42, -0.034914, 0.005619, -0.011297, -0.116599, -0.100569, -0.02566, 0.987741],
  ["ring_01_r", 51, -0.0806744, -0.00922, 0.0298306, -0.1586959, -0.0060697, 0.4301971, 0.8886558],
  ["ring_02_r", 52, -0.0276752, 0, 0, 0, 0, 0, 1],
  ["ring_03_r", 53, -0.0234174, 0, 0, 0, 0, 0, 1],
  ["thumb_01_r", 42, -0.0713822, 0.0101448, 0.0671414, 0.8995624, 0.243847, -0.1398371, 0.3343228],
  ["thumb_02_r", 55, -0.0275464, -1e-7, 0, 0, 0, 0, 1],
  ["thumb_03_r", 56, -0.0233084, 0, 0, 0, 0, 0, 1],
  ["index_metacarpal_r", 42, -0.035641, 0.003986, 0.024619, 0.028966, 0.063705, -0.007109, 0.997523],
  ["index_01_r", 58, -0.0889425, 0.0351816, 0.015344, -0.3341587, -0.1342831, 0.1424652, 0.9219597],
  ["index_02_r", 59, -0.0280499, 0, 0, 0, 0, 0, 1],
  ["index_03_r", 60, -0.0237347, 0, 0, 0, 0, 0, 1],
  ["upperarm_twist_01_r", 38, -0.074556, 0, -0.000001, -0.000054, -0.01879, -0.00285, 0.999819],
  ["upperarm_twist_02_r", 38, -0.149111, 0, -0.000002, 0, 0, 0, 1],
  ["thigh_r", 1, -0.016891, 0.000172, 0.095018, 0.007772, 0.07281, 0.997232, -0.012882],
  ["calf_r", 64, 0.486013, 0, 0, -0.000364, -0.059023, -0.102233, 0.993008],
  ["foot_r", 65, 0.367231, 0, 0.000001, 0.007746, 0.054473, -0.048136, 0.997324],
  ["ball_r", 66, 0.088267, 0.191882, -0.006786, 0.037801, 0.042159, -0.705077, 0.706866],
  ["calf_twist_02_r", 65, 0.12241, 0, 0.000328, -0.000093, 0.002251, 0.023249, 0.999727],
  ["calf_twist_01_r", 65, 0.244821, 0, 0.000657, -0.000093, 0.002251, 0.023249, 0.999727],
  ["thigh_twist_01_r", 64, 0.162004, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["thigh_twist_02_r", 64, 0.324008, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["thigh_l", 1, -0.016891, 0.000172, -0.095018, 0.07281, -0.007773, 0.012881, 0.997232],
  ["calf_l", 72, -0.486012, 0, -0.000001, -0.000364, -0.059024, -0.102233, 0.993008],
  ["foot_l", 73, -0.367231, 0, -0.000001, 0.007747, 0.054474, -0.048136, 0.997324],
  ["ball_l", 74, -0.088267, -0.191882, 0.006786, 0.0378, 0.042159, -0.705077, 0.706866],
  ["calf_twist_02_l", 73, -0.12241, 0, 0, -0.000093, 0.002251, 0.023249, 0.999727],
  ["calf_twist_01_l", 73, -0.244821, 0, -0.000656, -0.000093, 0.002251, 0.023249, 0.999727],
  ["thigh_twist_01_l", 72, -0.162004, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["thigh_twist_02_l", 72, -0.324008, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["ik_foot_root", 0, 0, 0, 0, 0, 0, 0, 1],
  ["ik_foot_l", 80, 0.140877, 0.009943, 0.082376, 0.063632, -0.704247, 0.041376, 0.705886],
  ["ik_foot_r", 80, -0.140876, 0.009848, 0.082382, 0.705887, -0.041376, -0.704247, -0.063632],
  ["ik_hand_root", 0, 0, 0, 0, 0, 0, 0, 1],
  ["ik_hand_gun", 83, -0.477683, -0.156988, 1.044664, 0.59846, -0.009649, 0.5206, 0.608874],
  ["ik_hand_l", 84, 0.437336, -0.616698, 0.584081, -0.457766, 0.645509, -0.611366, 0],
  ["ik_hand_r", 84, 0, 0, 0, 0, 0, 0, 1],
  ["interaction", 0, 0, 0, 0, 0, 0, 0, 1],
  ["center_of_mass", 0, 0, 0, 0, 0, 0, 0, 1],
  ["eye_l", 9, 0.078344, -0.090201, -0.035775, 0, 0, 0, 1],
  ["eye_r", 9, 0.078344, -0.090201, 0.035776, 0, 0, 0, 1],
  ["elbow_deform_l", 11, 0.2236661, 0, -1e-7, 0.060805, 0.057867, -0.2352289, 0.9683086],
  ["elbow_deform_r", 38, -0.2236669, 0, 0, 0.060804, 0.057867, -0.2352301, 0.9683083],
];

/** Simulated cloth chains riding the rig (driven by character/cloth.ts). One entry per
 *  cloth piece; bones are appended after the Manny + eye bones above. */
export interface MaleClothChain { anchor: string; bones: string[]; lens: number[] }
export interface MaleClothDef { chains: MaleClothChain[]; damping: number; gravity: number; radius: number; flutter: number; shape: number }
export const MALE_CLOTH: readonly MaleClothDef[] = [];
